import debounce from 'lodash.debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CategoryAutocompleteBaseProps,
  CategoryOption,
} from './category-autocomplete-base.component';
import { useController } from 'react-hook-form';
import intersectionBy from 'lodash.intersectionby';
import differenceBy from 'lodash.differenceby';
import {
  ListGroupsArgsType,
  ListItemsArgsType,
  ListMenusArgsType,
} from 'api/ordering-service/ordering-service.types';
import { useListSelectableItemsQuery } from 'api/ml-service/ml-service';

type UseCategoryAutocompleteConfig<OptionType> = {
  ITEM: {
    label: string;
    lazyQuery: [any, any, any];
    queryArgs: ListItemsArgsType;
    transformFn: (data: any) => OptionType[];
    inputTransformFn: (inputValue: string) => { [key: string]: string };
  };
  GROUP: {
    label: string;
    lazyQuery: [any, any, any];
    queryArgs: ListGroupsArgsType;
    transformFn: (data: any) => OptionType[];
    inputTransformFn: (inputValue: string) => { [key: string]: string };
  };
  CATALOG?: {
    label: string;
    lazyQuery: [any, any, any];
    queryArgs: ListMenusArgsType;
    transformFn: (data: any) => OptionType[];
    inputTransformFn: (inputValue: string) => { [key: string]: string };
  };
};

type UseCategoryAutocompleteWithoutSearchQueryConfg = {
  ITEM: {
    label: string;
  };
  GROUP: {
    label: string;
  };
  MENU: {
    label: string;
  };
};

const executeQuery = async (
  categoryObj,
  setLoading,
  setOptions,
  inputValue,
) => {
  const [trigger] = categoryObj.lazyQuery;
  try {
    const queryArgs = { ...categoryObj.queryArgs };
    Object.assign(queryArgs, categoryObj.inputTransformFn(inputValue));
    setLoading(true);
    const { data } = await trigger(queryArgs, true);
    setOptions(categoryObj.transformFn(data));
  } finally {
    setLoading(false);
  }
};

export const useCategoryAutocomplete = <OptionType extends CategoryOption>(
  name: string,
  config: UseCategoryAutocompleteConfig<OptionType>,
  defaultCategory,
): Omit<
  CategoryAutocompleteBaseProps<OptionType>,
  'helperText' | 'autoFocus'
> => {
  const configRef = useRef<UseCategoryAutocompleteConfig<OptionType>>(config);
  const [category, setCategory] = useState<string>(defaultCategory);
  const [options, setOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [inputValue, setInputValue] = useState<string>('');

  const categories = useMemo(
    () =>
      Object.entries(configRef.current).map(([id, { label }]) => ({
        id,
        label,
      })),
    [],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedQuery = useCallback(debounce(executeQuery, 1500), []);

  const { field, fieldState } = useController({ name });

  const allSelected =
    options.length === intersectionBy(field.value, options, 'id').length;

  const selectAll = () => {
    if (!allSelected) {
      field.onChange([
        ...field.value,
        ...differenceBy(options, field.value, 'id'),
      ]);
    } else {
      field.onChange(differenceBy(field.value, options, 'id'));
    }
  };

  const onChange = (_e, newValue) => {
    if (newValue.find(option => option.id === 'ALL')) {
      selectAll();
    } else {
      if (_e.key === 'Enter' || _e.key === 'Backspace') _e.preventDefault();
      else field.onChange(newValue);
    }
  };

  // Execute query on category change
  useEffect(() => {
    if (inputValue.length >= 2)
      executeQuery(
        configRef.current[category],
        setLoading,
        setOptions,
        inputValue,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Execute query on inputValue change
  useEffect(() => {
    if (inputValue.length >= 2)
      debouncedQuery(
        configRef.current[category],
        setLoading,
        setOptions,
        inputValue,
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  return {
    categories,
    category,
    setCategory,
    options,
    inputValue,
    setInputValue,
    value: field?.value,
    onChange,
    loading,
    error: fieldState?.error,
    allSelected,
  };
};

export const useCategoryAutocompleteWithoutSearchQuery = <
  OptionType extends CategoryOption,
>(
  name: string,
  config: UseCategoryAutocompleteWithoutSearchQueryConfg,
  defaultCategory,
): Omit<
  CategoryAutocompleteBaseProps<OptionType>,
  'helperText' | 'autoFocus'
> => {
  const configRef =
    useRef<UseCategoryAutocompleteWithoutSearchQueryConfg>(config);
  const [category, setCategory] = useState<string>(defaultCategory);
  const [options, setOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const { data, isLoading } = useListSelectableItemsQuery(null);
  const categories = useMemo(
    () =>
      Object.entries(configRef.current).map(([id, { label }]) => ({
        id,
        label,
      })),
    [],
  );

  const { field, fieldState } = useController({ name });

  const onChange = (_e, newValue) => {
    field.onChange(newValue);
  };

  useEffect(() => {
    switch (category) {
      case 'GROUP':
        setOptions(
          data?.groups.map(item => ({
            id: item,
            label: item,
            category: 'GROUP',
          })),
        );
        break;
      case 'ITEM':
        setOptions(
          data?.items.map(item => ({
            id: item,
            label: item,
            category: 'ITEM',
          })),
        );
        break;
      case 'MENU':
        setOptions(
          data?.menus.map(item => ({
            id: item,
            label: item,
            category: 'MENU',
          })),
        );
    }
  }, [category, data]);

  return {
    categories,
    category,
    setCategory,
    options,
    inputValue,
    setInputValue,
    value: field?.value,
    onChange,
    loading: isLoading,
    error: fieldState?.error,
    allSelected: false,
  };
};
